import { useEffect, useState } from "react";
import "./EditedFeatures.css";
import { StoreState } from "../../../store/rootReducer";
import { useSelector } from "react-redux";

import { NFSStatus } from "../../../utils/nfsUtils";
import Loader from "../../genericComponents/Loader/Loader";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import { FeatureWithChanges, findDifferences } from "../../../utils/newVersioningUtils";
import EditedFeaturePanelForNewJob from "./EditedFeaturePanelForNewJob";
import { applyValidationResults } from "../../../utils/jobsUtils";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface EditedFeaturesForNewJobProps {
    featuresAddedToJob: FeatureWithChanges[];
    setFeaturesAddedToJob: (features: FeatureWithChanges[]) => void;
    setLoading: (loading: boolean) => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const EditedFeaturesForNewJob = (props: EditedFeaturesForNewJobProps) => {
    const [featuresWithChanges, setFeaturesWithChanges] = useState<FeatureWithChanges[] | null>(null);

    const [isLoadingDiffs, setIsLoadingDiffs] = useState<boolean>(false);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const activeGuid = useSelector((state: StoreState) => state.version.activeGuid);

    const addFeatureToJob = (featureToAdd: FeatureWithChanges) => {
        const copiedFeaturesAddedToJob = [...props.featuresAddedToJob];
        const exists = copiedFeaturesAddedToJob.some(
            (f) =>
                f.mainFeature.layerId === featureToAdd.mainFeature.layerId &&
                f.mainFeature.objectId === featureToAdd.mainFeature.objectId
        );
        if (!exists) {
            copiedFeaturesAddedToJob.push(featureToAdd);
            props.setFeaturesAddedToJob(copiedFeaturesAddedToJob);
        }
    };

    const removeFeatureFromJob = (featureToRemove: FeatureWithChanges) => {
        const copiedFeaturesAddedToJob = [...props.featuresAddedToJob];
        const index = copiedFeaturesAddedToJob.findIndex(
            (f) =>
                f.mainFeature.layerId === featureToRemove.mainFeature.layerId &&
                f.mainFeature.objectId === featureToRemove.mainFeature.objectId
        );
        if (index > -1) {
            copiedFeaturesAddedToJob.splice(index, 1);
        }

        props.setFeaturesAddedToJob(copiedFeaturesAddedToJob);
    };

    const cannotAddFeatureToJob = (feature: FeatureWithChanges) => {
        if (props.featuresAddedToJob.length > 1) {
            return true;
        }
        if (props.featuresAddedToJob.length === 1) {
            const firstFeatureStatus = props.featuresAddedToJob[0].mainFeature.versionGraphic?.attributes.nfs_status;
            const secondFeatureStatus = feature.mainFeature.versionGraphic?.attributes.nfs_status;
            if (
                firstFeatureStatus === NFSStatus.PROJECTED &&
                (secondFeatureStatus === NFSStatus.PROJECTED_DISCONTINUED ||
                    secondFeatureStatus === NFSStatus.PROJECTED_REMOVED)
            ) {
                return false;
            } else if (
                (firstFeatureStatus === NFSStatus.PROJECTED_DISCONTINUED ||
                    firstFeatureStatus === NFSStatus.PROJECTED_REMOVED) &&
                secondFeatureStatus === NFSStatus.PROJECTED
            ) {
                return false;
            }
            return true;
        }

        return false;
    };

    const renderEditedFeatures = () => {
        const changeList: React.JSX.Element[] = [];
        if (!featuresWithChanges) return <div>Ingen endringer funnet</div>;
        for (let featureWithChange of featuresWithChanges) {
            changeList.push(
                <EditedFeaturePanelForNewJob
                    key={`${featureWithChange.mainFeature.layerId}-${featureWithChange.mainFeature.objectId}`}
                    editedFeature={featureWithChange}
                    addFeatureToJob={addFeatureToJob}
                    removeFeatureFromJob={removeFeatureFromJob}
                    featuresAddedToJob={props.featuresAddedToJob}
                    cannotAddFeatureToJob={cannotAddFeatureToJob(featureWithChange)}
                    storeHighlightHandle={props.storeHighlightHandle}
                    clearHighlightedFeatures={props.clearHighlightedFeatures}
                />
            );
        }

        if (changeList.length === 0) {
            return <div className="edited-features-no-changes">Ingen endringer funnet</div>;
        }

        return changeList;
    };

    const getValidationResults = async (objects: FeatureWithChanges[]) => {
        const versionName = activeVersion?.versionIdentifier.name ?? null;
        await applyValidationResults(objects, setFeaturesWithChanges, versionName, "PostToDefault");
    };

    const fetchDiffs = async () => {
        setIsLoadingDiffs(true);
        props.setLoading(true);
        try {
            const diffs = await findDifferences(activeGuid, activeVersion?.versionIdentifier.name!);
            setFeaturesWithChanges(diffs);
            console.log("Fetched diffs:", diffs);
            getValidationResults(diffs || []); // fire and forget
        } catch (error) {
            console.error("Error fetching diffs:", error);
        } finally {
            setIsLoadingDiffs(false);
            props.setLoading(false);
        }
    };

    useEffect(() => {
        fetchDiffs();
    }, [activeVersion]);

    return (
        <div className="edited-features w-100">
            <NfsButton disabled={isLoadingDiffs} onClick={fetchDiffs}>
                <span>{isLoadingDiffs ? "Laster..." : "Se etter nye endringer"}</span>
                {isLoadingDiffs && <Loader inline size={24} />}
            </NfsButton>
            <span className="text-center">Velg objekter til jobben:</span>
            <div className="edited-features-body">
                <div className="edited-features-content w-100 small-gap">{renderEditedFeatures()}</div>
            </div>
        </div>
    );
};

export default EditedFeaturesForNewJob;
